import {Icon} from '../ui/Icon';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import {OpeningTransactionEvidence} from './OpeningTransactionImport';
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { api, authenticatedFetch } from "../api";
import { Link } from "../ui/ScopedLink";
import { useStaffSession, useCapability } from "../ui/StaffSession";
import { PageHeader } from "../ui/PageHeader";
import { ErrorState, Loading, Empty } from "../ui/State";
import {
  WorkflowDraftForm,
  clearWorkflowDraft,
  readWorkflowDraft,
} from "../ui/WorkflowDraftForm";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateOnly, formatDateTime } from "../dates";
import "./income-control.css";
import { SummaryStrip } from "../ui/SummaryStrip";
import { DemandPdfButton } from "./DemandPdfButton";import { ArrearsReminders } from "./ArrearsReminders";
import { PeriodSelect } from "./PeriodSelect";
import { receiptPeriods, receiptPeriodForDate, staffIdByName, useArrangementDates } from "./income-defaults";

const today = () => new Date().toISOString().slice(0, 10);
const money = (v: unknown) => v===null||v===undefined||String(v).trim()===''||!Number.isFinite(Number(v))?'Not recorded':
 new Intl.NumberFormat("en-GB",{style:"currency",currency:"GBP"}).format(Number(v));
const stages = [
  "No action required",
  "Internal review",
  "Courtesy reminder due",
  "Formal reminder due",
  "Payment arrangement",
  "Disputed",
  "Director review",
  "Pre-legal review",
  "Solicitor referral approved",
  "Referred to solicitor",
  "On hold",
  "Resolved",
];
type Change = (path: string, body: any) => Promise<any>;
function ReviewForm({
  id,
  title,
  button = "Save for review",
  change,
  path,
  values,
  children,
  draftVersion = 1,
  onSaved,
  onDraftRestore,
}: {
  id: string;
  title: string;
  button?: string;
  change: Change;
  path: string;
  values: (f: FormData) => any;
  children: ReactNode;
  draftVersion?: number;
  onSaved?: () => void;
  onDraftRestore?: (values:Record<string,string[]>) => void;
}) {
  const [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [key, setKey] = useState(0);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const element = event.currentTarget,
      f = new FormData(element);
    setBusy(true);
    setError(undefined);
    try {
      await change(path, {
        ...values(f),
        reason: f.get("reason"),
        idempotencyKey: f.get("idempotencyKey"),
        confirm: f.get("confirm") === "on",
      });
      clearWorkflowDraft(id);
      onSaved?.();
      element.reset();
      setKey((x) => x + 1);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="income-action">
      <summary>{title}</summary>
      <WorkflowDraftForm
        key={key}
        draftKey={id}
        version={draftVersion}
        className="form-grid"
        onSubmit={save}
        onDraftRestore={onDraftRestore}
      >
        <input
          type="hidden"
          name="idempotencyKey"
          defaultValue={crypto.randomUUID()}
        />
        {children}
        <label className="full-width">
          Reason / evidence notes
          <textarea name="reason" minLength={5} maxLength={2000} required />
        </label>
        <label className="full-width">
          <input type="checkbox" name="confirm" required /> I have reviewed the
          amounts, sources and account context.
        </label>
        {Boolean(error) && <ErrorState error={error} />}
        <button disabled={busy}>{busy ? "Saving…" : button}</button>
      </WorkflowDraftForm>
    </details>
  );
}
export function IncomeControl() {
  const canManage = useCapability("finance:service-charge-manage"),
    canApprove = useCapability("finance:service-charge-approve"),
    canAllocate = useCapability("finance:bank-reconciliation-manage"),
    canReverse = useCapability("finance:bank-reconciliation-approve");
  const session = useStaffSession(),
    scope = session?.effectiveScope,
    building = scope?.kind === "building" ? scope : undefined;
  const [params, setParams] = useSearchParams(),
    [data, setData] = useState<any>(),
    [detail, setDetail] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [detailError, setDetailError] = useState<unknown>(),
    [revision, setRevision] = useState(0),
    [notice, setNotice] = useState(""),
    [suggestions, setSuggestions] = useState<any>(),
    [selectedSuggestion, setSelectedSuggestion] = useState<any>();
  const selectedReceipt = params.get("receipt") ?? "";
  const view = params.get("view") ?? "accounts",
    accountId = params.get("account") ?? "",
    asAt = params.get("asAt") ?? today(),
    from = params.get("from") ?? "0001-01-01";
  const root = building ? `/api/finance/income/${building.id}` : "";
  const showStatement=Boolean(accountId&&(view==="accounts"||view==="arrears"));
  const url = (path: string) =>
    `${root}${path}?building=${building?.id}&asAt=${asAt}&from=${from}&ledgerPage=${params.get("ledgerPage") ?? 1}&transactionType=${params.get("transactionType") ?? ""}${params.get("postingStatus") ? "&postingStatus=" + params.get("postingStatus") : ""}${params.get("accountingPeriodId") ? "&accountingPeriodId=" + params.get("accountingPeriodId") : ""}`;
  const filter = params.toString();
  useEffect(() => {
    let active = true;
    setData(undefined);
    setError(undefined);
    const requestParams = new URLSearchParams(filter);
    if (building) requestParams.set("building", building.id);
    requestParams.set("asAt", asAt);
    if (building)
      api<any>(`${root}?${requestParams}`)
        .then((d) => {
          if (active) {
            if (
              d.building !== building.id ||
              d.items.some((a: any) => a.development_id !== building.id) ||
              d.receipts.some((r: any) => r.development_id !== building.id)
            )
              throw Error("The results do not match this building.");
            setData({
              ...d,
              permissions: {
                manage: d.permissions.manage && canManage,
                approve: d.permissions.approve && canApprove,
                allocate: d.permissions.allocate && canAllocate,
                reverse: d.permissions.reverse && canReverse,
              },
            });
          }
        })
        .catch((e) => {
          if (active) setError(e);
        });
    return () => {
      active = false;
    };
  }, [
    building?.id,
    filter,
    revision,
    canManage,
    canApprove,
    canAllocate,
    canReverse,
  ]);
  useEffect(() => {
    let active = true;
    setDetail(undefined);
    setDetailError(undefined);
    if (building && accountId)
      api<any>(url("/accounts/" + accountId))
        .then((d) => {
          if (active) {
            if (
              d.account.id !== accountId ||
              d.building !== building.id ||
              d.account.development_id !== building.id ||
              d.entries.some((p: any) => p.account_id !== accountId)
            )
              throw Error("Account context changed.");
            setDetail(d);
          }
        })
        .catch((e) => {
          if (active) setDetailError(e);
        });
    return () => {
      active = false;
    };
  }, [
    building?.id,
    accountId,
    asAt,
    from,
    params.get("ledgerPage"),
    params.get("transactionType"),
    params.get("postingStatus"),
    params.get("accountingPeriodId"),
    revision,
  ]);
  function select(key: string, value: string) {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    if (key !== "page") p.delete("page");
    if (!["ledgerPage", "receipt", "view"].includes(key))
      p.delete("ledgerPage");
    if (key === "bankAccountId") p.delete("receiptPage");
    setParams(p);
  }
  const change: Change = async (path, body) => {
    const result = await api<any>(url(path), {
      method: "POST",
      body: JSON.stringify(body),
    });
    setNotice(
      path === "/receipts/propose"
        ? "Suggested matches are ready for review."
        : "Saved. The account history is retained. No message or payment was sent.",
    );
    if (path !== "/receipts/propose") setRevision((x) => x + 1);
    return result;
  };
  if (!building)
    return (
      <section className="income-empty">
        <PageHeader
          title="Service-charge accounts"
          scope={{ kind: "portfolio" }}
          context="Choose a building to review unit accounts, receipts and arrears."
        />
        <Empty>Use the building selector to open its financial accounts.</Empty>
      </section>
    );
  return (
    <div className="finance-scoped-page income-page">
      {!showStatement&&(<PageHeader
        title="Service-charge accounts"
        scope={{ kind: "building", name: building.name }}
        context="Unit liabilities, bank receipts and internal arrears review."
      />)}
      {showStatement&&(!data||!detail||detail.account.id!==accountId||detail.building!==building.id)&&<PageHeader title="Account statement" secondaryActions={[{kind:"button",label:"Back to accounts",onClick:()=>select("account","")}]}/>}
          {showStatement && data && data.building===building.id && (
            <section className="panel selected-account-statement" aria-label="Selected account statement">
              {detailError ? (
                <ErrorState error={detailError} />
              ) : !detail || detail.account.id!==accountId || detail.building!==building.id ? (
                <Loading />
              ) : (
                <AccountDetail
                  key={detail.account.id}
                  detail={detail}
                  data={data}
                  root={root}
                  change={change}
                  url={url}
                  select={select}
                />
              )}
            </section>
          )}
      {showStatement&&!data&&!error&&<Loading/>}
 <details className="account-register-context" open={!showStatement||Boolean(error)||undefined}><summary>Account register and filters</summary>
      <div className="actions">
        <Link className="button-link secondary" to="/finance/operations">
          <Icon name="arrow-left"/> Finance
        </Link>
        <Link
          className="button-link secondary"
          to="/finance/service-charges/demand-batches"
        >
          Residential demand batches
        </Link>
        <Link className="button-link secondary" to="/finance/reconciliation">
          Bank reconciliation
        </Link>
        <Link className="button-link secondary" to="/finance/accounts-payable">
          Supplier invoices
        </Link>
        <Link to={`/buildings/${building.id}/setup/financials?section=opening`}>
          View or amend unit accounts, periods and opening balances
        </Link>
      </div>
      <nav className="actions" aria-label="Income views">
        {[
          ["accounts", "Accounts"],
          ["receipts", "Receipts"],
          ["arrears", "Arrears"],
          ["controls", "Control totals"],
        ].map(([id, label]) => (
          <button
            className={view === id ? "" : "secondary"}
            aria-pressed={view === id}
            key={id}
            onClick={() => select("view", id!)}
          >
            {label}
          </button>
        ))}
      </nav>
      {notice && (
        <p role="status" className="notice">
          {notice}
        </p>
      )}
      <form
        className="income-filters"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget),
            p = new URLSearchParams(params);
          for (const key of [
            "search",
            "asAt",
            "from",
            "stage",
            "classification",
            "disputed",
            "arrangement",
            "schedule",
            "ageing",
            "assigned",
          ]) {
            const v = String(f.get(key) ?? "");
            if (v) p.set(key, v);
            else p.delete(key);
          }
          p.delete("page");
          setParams(p);
        }}
      >
        <label>
          Unit / legal owner
          <input name="search" defaultValue={params.get("search") ?? ""} />
        </label>
        <label>
          As at
          <DateOnlyInput name="asAt" defaultValue={asAt} required />
        </label>
        <details className="income-advanced-filters"><summary>More filters</summary><div className="income-advanced-filter-fields">
        <label>
          Statement from
          <DateOnlyInput
            name="from"
            defaultValue={from === "0001-01-01" ? "" : from}
          />
        </label>
        <label>
          Classification
          <select
            name="classification"
            defaultValue={params.get("classification") ?? ""}
          >
            <option value="">All</option>
            <option>Residential</option>
            <option>Commercial</option>
          </select>
        </label>
        <label>
          Stage
          <select name="stage" defaultValue={params.get("stage") ?? ""}>
            <option value="">All stages</option>
            {stages.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Dispute
          <select name="disputed" defaultValue={params.get("disputed") ?? ""}>
            <option value="">All</option>
            <option value="true">Disputed</option>
            <option value="false">Undisputed</option>
          </select>
        </label>
        <label>
          Arrangement
          <select
            name="arrangement"
            defaultValue={params.get("arrangement") ?? ""}
          >
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">None active</option>
          </select>
        </label>
        <label>
          Schedule
          <select name="schedule" defaultValue={params.get("schedule") ?? ""}>
            <option value="">All schedules</option>
            {Array.from(
              new Map<string, string>(
                (data?.accountChoices ?? []).map((a: any) => [
                  a.schedule_id,
                  a.schedule_name,
                ]),
              ).entries(),
            ).map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Ageing
          <select name="ageing" defaultValue={params.get("ageing") ?? ""}>
            <option value="">All ages</option>
            {[
              "Not yet due",
              "Due today",
              "1–30 days",
              "31–60 days",
              "61–90 days",
              "91–180 days",
              "Over 180 days",
            ].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          Responsible staff
          <select name="assigned" defaultValue={params.get("assigned") ?? ""}>
            <option value="">All staff</option>
            {data?.staff?.map((u: any) => (
              <option value={u.id} key={u.id}>
                {u.display_name}
              </option>
            ))}
          </select>
        </label>
        </div></details>
        <button className="secondary">Apply filters</button>
      </form>
      {error ? (
        <ErrorState error={error} onRetry={() => setRevision((x) => x + 1)} />
      ) : !data ? (
        <Loading />
      ) : (
        <>
          <SummaryStrip
            mode="metric"
            ariaLabel="Income position"
            items={[
              { label: "Outstanding", value: money(data.totals.balance) },
              { label: "Due", value: money(data.totals.due) },
              { label: "Not yet due", value: money(data.totals.notYetDue) },
              { label: "Disputed", value: money(data.totals.disputed) },
              {
                label: "Account credit",
                value: money(data.totals.unallocatedCredit),
              },
              { label: "Net arrears", value: money(data.totals.netArrears) },
            ]}
          />
          {view === "arrears" && building && <ArrearsReminders building={building.id} />}{(view === "accounts" || view === "arrears") && (
            <section className="panel">
              <h2>
                {view === "arrears"
                  ? "Building arrears review"
                  : "Unit accounts"}
              </h2>
              <p>
                {data.total} accounts · {data.accountsInArrears} with net
                arrears. Stages record internal workflow; they do not prove a
                reminder or referral was sent.
              </p>
              {!data.items.length ? (
                <Empty>
                  No accounts match this view.
                </Empty>
              ) : (
                <div className="table-wrap">
                  <ResponsiveTable>
                    <thead>
                      <tr>
                        <th>Unit / schedule</th>
                        <th>Legal liable party</th>
                        <th>Balance</th>
                        <th>Due</th>
                        <th>Disputed</th>
                        <th>Oldest due</th>
                        <th>Last receipt</th>
                        <th>Stage / arrangement</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((a: any) => (
                        <tr key={a.id}>
                          <td>
                            {a.premises_name}
                            <small>{a.schedule_name}</small>
                          </td>
                          <td>
                            {a.legal_owner_name ??
                              "Requires legal-owner review"}
                          </td>
                          <td>{money(a.balance)}</td>
                          <td>{money(a.due)}</td>
                          <td>{money(a.disputed)}</td>
                          <td>
                            {a.oldestDue ? formatDateOnly(a.oldestDue) : "—"}
                          </td>
                          <td>
                            {a.lastReceipt
                              ? formatDateOnly(a.lastReceipt)
                              : "—"}
                          </td>
                          <td>
                            {a.arrears_stage ?? "No action required"}
                            <small>
                              {a.arrangement
                                ? `Arrangement: ${a.arrangement.ownershipNeedsReview ? "ownership needs review" : a.arrangement.potentiallyMissed ? "payment needs review" : "on track"}`
                                : "No active arrangement"}
                            </small>
                          </td>
                          <td>
                            <button
                              className="secondary"
                              onClick={() => select("account", a.id)}
                            >
                              Open {a.premises_name}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ResponsiveTable>
                </div>
              )}
              <div className="actions">
                <button
                  className="secondary"
                  disabled={data.page === 1}
                  onClick={() => select("page", String(data.page - 1))}
                >
                  Previous
                </button>
                <span>Page {data.page}</span>
                <button
                  className="secondary"
                  disabled={data.page * data.pageSize >= data.total}
                  onClick={() => select("page", String(data.page + 1))}
                >
                  Next
                </button>
              </div>
            </section>
          )}
          {view === "receipts" && (
            <section className="panel">
              <h2>Incoming bank receipts</h2>
              <p>
                Choose an imported incoming transaction, review evidence and
                confirm its allocation. Import additional statements in{" "}
                <Link to="/finance/reconciliation">Bank reconciliation</Link>.
              </p>
              <label>
                Bank account
                <select
                  value={params.get("bankAccountId") ?? ""}
                  onChange={(e) => select("bankAccountId", e.target.value)}
                >
                  <option value="">All building bank accounts</option>
                  {data.banks.map((b: any) => (
                    <option key={b.id} value={b.id}>
                      {b.account_name}
                    </option>
                  ))}
                </select>
              </label>
              {data.moreReceipts && (
                <p role="status">
                  Showing the latest 250 receipts. Select a bank account to
                  narrow the view.
                </p>
              )}
              <div className="table-wrap">
                <ResponsiveTable>
                  <thead>
                    <tr>
                      <th>Date / bank</th>
                      <th>Reference / remitter</th>
                      <th>Amount</th>
                      <th>Unallocated</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.receipts.map((r: any) => (
                      <tr key={r.id}>
                        <td>
                          {formatDateOnly(r.transaction_date)}
                          <small>{r.account_name}</small>
                        </td>
                        <td>
                          {r.bank_reference ?? r.description}
                          <small>
                            {r.counterparty ?? "Remitter not recorded"}
                          </small>
                        </td>
                        <td>{money(r.amount)}</td>
                        <td>{money(r.available_amount)}</td>
                        <td>
                          {Number(r.available_amount) === 0
                            ? `${r.classification} · Fully attributed`
                            : r.classification !== "Unreviewed"
                              ? r.classification
                              : r.available_amount === r.amount
                                ? "Unreviewed"
                                : "Partially allocated"}
                        </td>
                        <td>
                          <button
                            className="secondary"
                            onClick={() => {
                              select("receipt", r.id);
                              setSelectedSuggestion(undefined);
                              setSuggestions(undefined);
                            }}
                          >
                            Review receipt
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ResponsiveTable>
              </div>
              <div className="actions">
                <button
                  className="secondary"
                  disabled={(data.receiptPage ?? 1) <= 1}
                  onClick={() =>
                    select("receiptPage", String(data.receiptPage - 1))
                  }
                >
                  Previous receipts
                </button>
                <span>Receipt page {data.receiptPage ?? 1}</span>
                <button
                  className="secondary"
                  disabled={!data.moreReceipts}
                  onClick={() =>
                    select("receiptPage", String((data.receiptPage ?? 1) + 1))
                  }
                >
                  Older receipts
                </button>
              </div>
              {selectedReceipt &&
                data.receipts.find((r: any) => r.id === selectedReceipt) && (
                  <>
                    <ReceiptAllocation
                      key={
                        selectedReceipt +
                        ":" +
                        (selectedSuggestion?.key ?? revision)
                      }
                      suggestion={selectedSuggestion}
                      transaction={data.receipts.find(
                        (r: any) => r.id === selectedReceipt,
                      )}
                      accounts={data.accountChoices ?? data.items}
                      periods={data.periods}
                      root={root}
                      building={building.id}
                      asAt={asAt}
                      change={change}
                      canAllocate={data.permissions.allocate}
                    />
                    {data.permissions.allocate && (
                      <button
                        className="secondary"
                        onClick={() =>
                          void change("/receipts/propose", {
                            transactionId: selectedReceipt,
                            asAt,
                            reason:
                              "Review matching evidence without confirming allocation",
                            idempotencyKey: crypto.randomUUID(),
                            confirm: true,
                          })
                            .then(setSuggestions)
                            .catch(setError)
                        }
                      >
                        Review suggested matches
                      </button>
                    )}
                    {suggestions && (
                      <section aria-label="Suggested receipt matches">
                        <h3>Suggested matches — confirmation required</h3>
                        {suggestions.suggestions.length ? (
                          suggestions.suggestions.map((s: any) => (
                            <article className="queue-card" key={s.accountId}>
                              <strong>
                                {s.unit} · {s.confidence} confidence
                              </strong>
                              <p>{s.evidence.join("; ")}</p>
                              <p>
                                {s.charges.length} proposed charge allocations ·
                                excess on account {money(s.onAccount)}
                              </p>
                              <button
                                className="secondary"
                                onClick={() =>
                                  setSelectedSuggestion({
                                    ...s,
                                    key: crypto.randomUUID(),
                                  })
                                }
                              >
                                Use this editable proposal
                              </button>
                              {s.charges.map((c: any) => (
                                <p key={c.postingId}>
                                  {c.method}: {money(c.amount)}
                                </p>
                              ))}
                            </article>
                          ))
                        ) : (
                          <p>
                            No sufficiently evidenced candidate. Leave
                            unidentified or select a verified account manually.
                          </p>
                        )}
                      </section>
                    )}
                  </>
                )}
            </section>
          )}
          {view === "controls" && (
            <section className="panel">
              <h2>Reconciliation and exceptions</h2>
              <p>
                Account balances and bank attribution are separate controls.
                Timing, unallocated receipts and account credits explain
                differences.
              </p>
              <dl className="income-totals">
                {Object.entries(data.totals).map(([k, v]) => (
                  <div key={k}>
                    <dt>
                      {
                        (
                          {
                            openingBalances: "Brought-forward balances",
                            debitAdjustments: "Debit adjustments",
                            otherCredits: "Credits and write-offs",
                            controlDifference: "Ledger control difference",
                            totalCharges: "Issued charges",
                            totalReceipts: "Bank receipts on accounts",
                            totalAllocated: "Receipts allocated to charges",
                            balance: "Closing account balances",
                            due: "Currently due",
                            notYetDue: "Not yet due",
                            disputed: "Disputed",
                            unallocatedCredit: "Unallocated account credit",
                            arrangementCovered: "Arrangement cover",
                            netArrears: "Net arrears",
                          } as any
                        )[k]
                      }
                    </dt>
                    <dd>{money(v)}</dd>
                  </div>
                ))}
              </dl>
              <h3>Bank control by client account</h3>
              {data.bankControl.map((b: any) => (
                <article key={b.id}>
                  <strong>{b.account_name}</strong>
                  <p>
                    Imported {money(b.imported)} = service-charge receipts{" "}
                    {money(b.service_charge_receipts)} + other classified income{" "}
                    {money(b.classified_other)} + unallocated{" "}
                    {money(b.unallocated)}
                  </p>
                </article>
              ))}
              <h3>Exceptions</h3>
              {data.exceptions.length ? (
                <ul>
                  {data.exceptions.map((x: any, i: number) => (
                    <li key={i}>
                      {x.kind}{" "}
                      {x.accountId && (
                        <button
                          className="secondary"
                          onClick={() => {
                            select("account", x.accountId);
                          }}
                        >
                          Review account
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>
                  No exceptions detected by the available source and balance
                  checks.
                </p>
              )}
            </section>
          )}
          {data.permissions.manage && (
            <section className="panel">
              <h2>Opening balances and adjustments</h2>
              <p><Link to={`/buildings/${building.id}/setup/financials?section=opening-import`}>Import inherited arrears transactions</Link></p>
              <p>
                Record brought-forward evidence or propose a correction. A
                separate authorised reviewer must approve it before it affects
                the ledger.
              </p>
              <ProposalForm
                building={building.id}
                accounts={data.accountChoices ?? data.items}
                root={root}
                asAt={asAt}
                periods={data.periods}
                documents={data.documents ?? []}
                change={change}
              />
              {data.proposals.map((p: any) => (
                <article className="queue-card" key={p.id}>
                  <strong>
                    {p.premises_name} · {p.kind} · {money(p.amount)} ·{" "}
                    {p.status}
                  </strong>
                  <p>
                    {p.source_reference} · {p.provenance}
                  </p>
                  <p>
                    {p.legal_owner_name} · {formatDateOnly(p.effective_date)}
                  </p>
                  <OpeningTransactionEvidence source={p.opening_source_context} documentId={p.source_document_id} fundNames={p.opening_source_funds}/>
                  {p.status === "Draft" && data.permissions.approve && (
                    <ReviewForm
                      id={"income-approval:" + p.id}
                      title="Review proposal"
                      button="Record decision"
                      path={"/adjustments/" + p.id + "/decision"}
                      change={change}
                      values={(f) => ({ decision: f.get("decision") })}
                    >
                      <label>
                        Decision
                        <select name="decision">
                          <option>Approve</option>
                          <option>Reject</option>
                        </select>
                      </label>
                    </ReviewForm>
                  )}
                </article>
              ))}
            </section>
          )}
        </>
      )}
    </details></div>
  );
}
function AccountDetail({
  detail: d,
  data,
  root,
  change,
  url,
  select,
}: {
  select: (key: string, value: string) => void;
  detail: any;
  data: any;
  root: string;
  change: Change;
  url: (p: string) => string;
}) {
  const [filters] = useSearchParams();
  const [error, setError] = useState<unknown>();
  const [reverseDate, setReverseDate] = useState(today());
  // F-123: first payment follows the start date and completion is worked out from the instalments; both can be overtyped.
  const arr = useArrangementDates();
  const session = useStaffSession();
  const correspondence = d.account.correspondence;
  const correspondenceAddress =
    correspondence?.correspondenceAddress ??
    correspondence?.postalAddress ??
    correspondence?.address;
  const address = d.account.building_snapshot;
  async function download(format: string) {
    try {
      const r = await authenticatedFetch(
        url("/accounts/" + d.account.id + "/export") + "&format=" + format,
      );
      if (!r.ok)
        throw Error("Statement export failed. Please refresh and try again.");
      if (
        !r.headers
          .get("content-type")
          ?.includes(format === "pdf" ? "application/pdf" : "text/csv")
      )
        throw Error(
          "The server did not return the requested statement format.",
        );
      const blob = await r.blob(),
        href = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = href;
      a.download = `statement-${d.asAt}.${format}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(href), 30000);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <>
      <PageHeader title={d.account.premises_name} context={`${d.account.legal_owner_name??'Legal liable party requires review'} · ${d.account.account_reference}`}
 primaryAction={{kind:'button',label:'Export statement PDF',disabled:d.account.account_kind==='Original non-unit receivable',onClick:()=>void download('pdf')}}
 secondaryActions={[{kind:'button',label:'Export CSV',onClick:()=>void download('csv')},{kind:'button',label:'Back to accounts',onClick:()=>select('account','')}]}/>
 <details className="statement-source-facts"><summary>Account parties and statement basis</summary>
      <div className="record-heading">
        <div>
          <span className="eyebrow">Account statement · Preview</span>
          <h2>{d.account.premises_name}</h2>
          <p>
            {d.account.client_name ?? "Client legal entity requires review"} ·{" "}
            {d.account.building_name}
          </p>
          {address && (
            <p>
              {[
                address.line_1,
                address.line_2,
                address.town_city,
                address.county,
                address.postcode,
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
          )}
          {typeof correspondenceAddress === "string" && (
            <p>Correspondence: {correspondenceAddress}</p>
          )}
          <p>
            {d.account.legal_owner_name ?? "Legal liable party requires review"}
          </p>
          <p>
            {d.account.schedule_name} · {d.account.account_reference}
          </p>
        </div>
        <strong>{money(d.balance)}</strong>
      </div>
 </details>
      {Boolean(error) && <ErrorState error={error} />}
      <SummaryStrip mode="metric" ariaLabel="Selected account balances" items={[
 {label:'Opening',value:money(d.openingBalance)}, {label:'Closing',value:money(d.balance)},
 {label:'Due',value:money(d.due)}, {label:'Not yet due',value:money(d.notYetDue)}, {label:'Disputed',value:money(d.disputed)},
 ]}/>
      <div
        className="table-wrap"
        role="region"
        aria-label="Ledger postings, scroll horizontally for all columns"
        tabIndex={0}
      >
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Date / status</th>
              <th>Transaction / reference</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Running balance</th>
              <th>Source / correction</th>
            </tr>
          </thead>
          <tbody>
            {d.entries.map((p: any) => (
              <tr key={p.id}>
                <td>
                  {formatDateOnly(p.posting_date)}
                  <small>{p.status}</small>
                </td>
                <td>
                  {p.posting_type}
                  <small>{p.narrative}</small>
                  <small>{p.demand_reference ?? p.external_reference}</small>
                </td>
                <td>{money(p.debit_amount)}</td>
                <td>{money(p.credit_amount)}</td>
                <td>{money(p.running_balance)}</td>
                <td>
                  {p.demand_pack_id && (
                    <Link
                      to={`/finance/service-charges/demand-batches?batch=${p.demand_batch_id}`}
                    >
                      Demand calculation and history
                    </Link>
                  )}
                  {p.demand_batch_id && p.demand_pack_id && (
                    <DemandPdfButton
                      url={`/api/finance/demand-workflow/${d.account.development_id}/batches/${p.demand_batch_id}/packs/${p.demand_pack_id}/pdf?building=${d.account.development_id}`}
                      label={"Exact PDF " + p.demand_reference}
                      filename={p.demand_reference + ".pdf"}
                    />
                  )}
                  {p.bank_transaction_id && (
                    <Link
                      to={`/finance/reconciliation?transaction=${p.bank_transaction_id}`}
                    >
                      Bank transaction
                    </Link>
                  )}
                  {p.source_document_id && (
                    <Link to={`/documents/${p.source_document_id}`}>
                      Supporting document
                    </Link>
                  )}
                  {p.budget_id && (
                    <Link
                      to={`/finance/operations?view=budgets&budget=${p.budget_id}`}
                    >
                      Budget
                    </Link>
                  )}
                  {p.reverses_posting_id && (
                    <small>Reverses {p.reverses_posting_id}</small>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
      <div className="actions">
        <label>
          Transaction type
          <select
            value={filters.get("transactionType") ?? ""}
            onChange={(e) => select("transactionType", e.target.value)}
          >
            <option value="">All types</option>
            {[
              "Opening balance",
              "Charge",
              "Receipt",
              "Credit",
              "Adjustment",
              "Reversal",
              "Transfer",
              "Refund record",
              "Write-off",
            ].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
        <label>
          Posting status
          <select
            value={filters.get("postingStatus") ?? ""}
            onChange={(e) => select("postingStatus", e.target.value)}
          >
            <option value="">All statuses</option>
            <option>Posted</option>
            <option>Reversed</option>
            <option>Reversal</option>
          </select>
        </label>
        <label>
          Ledger financial period
          <select
            value={filters.get("accountingPeriodId") ?? ""}
            onChange={(e) => select("accountingPeriodId", e.target.value)}
          >
            <option value="">All periods</option>
            {data.periods
              .filter((p: any) => p.schedule_id === d.account.schedule_id)
              .map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
          </select>
        </label>
      </div>
      <p>
        Transaction filters apply to the ledger list. Balances and exports cover
        the full selected date range.
      </p>
      <div className="actions">
        <button
          className="secondary"
          disabled={(d.ledgerPage ?? 1) <= 1}
          onClick={() => select("ledgerPage", String(d.ledgerPage - 1))}
        >
          Earlier ledger page
        </button>
        <span>
          {d.entryTotal ?? d.entries.length} transactions · page{" "}
          {d.ledgerPage ?? 1}
        </span>
        <button
          className="secondary"
          disabled={
            (d.ledgerPage ?? 1) * 100 >= (d.entryTotal ?? d.entries.length)
          }
          onClick={() => select("ledgerPage", String(d.ledgerPage + 1))}
        >
          Next ledger page
        </button>
      </div>
      <details>
        <summary>Charges, settlement and ageing</summary>
        <div className="table-wrap">
          <ResponsiveTable>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Due</th>
                <th>Paid</th>
                <th>Remaining</th>
                <th>Ageing</th>
                <th>Disputed</th>
              </tr>
            </thead>
            <tbody>
              {d.charges.map((c: any) => (
                <tr key={c.id}>
                  <td>
                    {c.demand_reference ??
                      c.external_reference ??
                      c.posting_type}
                  </td>
                  <td>{formatDateOnly(c.due_date)}</td>
                  <td>{money(c.paid_amount)}</td>
                  <td>{money(c.remaining_amount)}</td>
                  <td>{c.ageing_band}</td>
                  <td>{money(c.disputed_amount)}</td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </div>
      </details>
      {data.permissions.allocate && (
        <ReviewForm
          id={"income-credit:" + d.account.id}
          title="Allocate account credit to charges"
          button="Confirm allocation"
          path="/credits/allocate"
          change={change}
          values={(f) => ({
            accountId: d.account.id,
            creditId: f.get("creditId"),
            effectiveDate: f.get("date"),
            charges: d.charges
              .filter((c: any) => Number(f.get("charge:" + c.id)) > 0)
              .map((c: any) => ({
                postingId: c.id,
                amount: String(f.get("charge:" + c.id)),
                method: "Manual",
              })),
          })}
        >
          <label>
            Credit
            <select name="creditId" required>
              {(d.creditChoices ?? d.entries)
                .filter(
                  (p: any) =>
                    Number(p.credit_amount) > 0 && p.status === "Posted",
                )
                .map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.posting_type} · {money(p.credit_amount)} ·{" "}
                    {p.external_reference}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Effective date
            <DateOnlyInput name="date" defaultValue={today()} required />
          </label>
          {d.charges
            .filter((c: any) => Number(c.remaining_amount) > 0)
            .map((c: any) => (
              <label key={c.id}>
                {c.demand_reference ?? c.posting_type} ·{" "}
                {money(c.remaining_amount)} remaining
                <input
                  name={"charge:" + c.id}
                  type="number"
                  min="0"
                  step=".01"
                  defaultValue="0.00"
                />
              </label>
            ))}
        </ReviewForm>
      )}
      {data.permissions.reverse && (
        <>
          <ReviewForm
            id={"income-unallocate:" + d.account.id}
            title="Reverse a charge allocation"
            path="/allocations/reverse"
            change={change}
            values={(f) => ({
              allocationId: f.get("allocationId"),
              effectiveDate: f.get("date"),
            })}
          >
            <label>
              Allocation
              <select name="allocationId" required>
                {d.allocations
                  .filter(
                    (a: any) =>
                      !a.reverses_allocation_id &&
                      !d.allocations.some(
                        (r: any) => r.reverses_allocation_id === a.id,
                      ),
                  )
                  .map((a: any) => (
                    <option key={a.id} value={a.id}>
                      {formatDateOnly(a.effective_date)} · {money(a.amount)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Effective date
              <DateOnlyInput name="date" defaultValue={today()} required />
            </label>
          </ReviewForm>
          <ReviewForm
            id={"income-reverse:" + d.account.id}
            title="Reverse a receipt or adjustment"
            path="/postings/reverse"
            change={change}
            values={(f) => ({
              postingId: f.get("postingId"),
              periodId: f.get("periodId"),
              effectiveDate: f.get("date"),
            })}
          >
            <label>
              Posting
              <select name="postingId" required>
                {(d.reversalChoices ?? d.entries)
                  .filter(
                    (p: any) => p.status === "Posted" && !p.demand_pack_id,
                  )
                  .map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.posting_type} · {p.narrative}
                    </option>
                  ))}
              </select>
            </label>
            <PeriodSelect
              periods={data.periods.filter(
                (p: any) => p.schedule_id === d.account.schedule_id,
              )}
              date={reverseDate}
            />
            <label>
              Effective date
              <DateOnlyInput name="date" defaultValue={today()} required onChange={setReverseDate} />
            </label>
          </ReviewForm>
        </>
      )}
      {data.permissions.manage && (
        <>
          <ReviewForm
            id={"income-dispute:" + d.account.id}
            title="Record a disputed amount"
            path="/disputes"
            change={change}
            values={(f) => ({
              accountId: d.account.id,
              postingId: f.get("postingId"),
              amount: f.get("amount"),
              raisedOn: f.get("date"),
              responsibleUserId: f.get("responsibleUserId") || null,
              documentId: f.get("documentId") || null,
            })}
          >
            <label>
              Charge
              <select name="postingId" required>
                {d.charges
                  .filter((c: any) => Number(c.remaining_amount) > 0)
                  .map((c: any) => (
                    <option value={c.id} key={c.id}>
                      {c.demand_reference ?? c.posting_type} ·{" "}
                      {money(c.remaining_amount)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Amount disputed
              <input
                name="amount"
                type="number"
                step=".01"
                min=".01"
                required
              />
            </label>
            <label>
              Date raised
              <DateOnlyInput name="date" defaultValue={today()} required />
            </label>
            <label>
              Dispute responsible person
              <select name="responsibleUserId" defaultValue={staffIdByName(data.staff, session?.displayName)}>
                <option value="">Unassigned</option>
                {data.staff?.map((u: any) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name}
                  </option>
                ))}
              </select>
            </label>
            <Evidence documents={data.documents ?? []} />
          </ReviewForm>
          <ReviewForm
            id={"income-stage:" + d.account.id}
            title="Update arrears review stage"
            path="/arrears"
            change={change}
            values={(f) => ({
              accountId: d.account.id,
              stage: f.get("stage"),
              dueDate: f.get("date") || null,
              holdReason: f.get("stage") === "On hold" ? f.get("reason") : "",
              assignedTo: f.get("assignedTo") || null,
              createInternalTask: f.get("createTask") === "on",
              documentId: f.get("documentId") || null,
            })}
          >
            <label>
              Stage
              <select
                name="stage"
                defaultValue={d.account.arrears_stage ?? "Internal review"}
              >
                {stages.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Review due
              <DateOnlyInput name="date" />
            </label>
            <label>
              Responsible staff member
              <select name="assignedTo">
                <option value="">Unassigned</option>
                {data.staff?.map((u: any) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name}
                  </option>
                ))}
              </select>
            </label>
            <Evidence documents={data.documents ?? []} />
            <label>
              <input type="checkbox" name="createTask" /> Create an internal
              review task
            </label>
          </ReviewForm>
        </>
      )}
      {d.disputes.map((p: any) => (
        <article key={p.id}>
          <p>
            Dispute: {money(p.amount)} · {p.status} · {p.reason}
          </p>
          {p.status === "Open" && data.permissions.manage && (
            <ReviewForm
              id={"resolve:" + p.id}
              title="Resolve dispute"
              path={"/disputes/" + p.id + "/resolve"}
              change={change}
              values={(f) => ({ resolvedOn: f.get("date") })}
            >
              <label>
                Resolution date
                <DateOnlyInput name="date" defaultValue={today()} required />
              </label>
            </ReviewForm>
          )}
        </article>
      ))}
      {data.permissions.approve && <p className="form-help">Recording an arrangement also approves it in your name. This is allowed for now; where possible ask a second person to agree arrangements. It is recorded as approved by the person who recorded it.</p>}
      {data.permissions.approve && (
        <ReviewForm
          id={"arrangement:" + d.account.id}
          draftVersion={Number.parseInt(d.account.arrangementOwnerContext?.sourceHash.slice(0, 12) ?? '1', 16)}
          title="Record an agreed payment arrangement"
          onSaved={arr.reset}
          onDraftRestore={arr.restore}
          path="/arrangements"
          change={change}
          values={(f) => ({
            accountId: d.account.id,
            ownerInterestId:f.get("ownerInterestId")||undefined,
            ownerSourceHash:d.account.arrangementOwnerContext?.sourceHash,
            startsOn: f.get("start"),
            firstPaymentOn: f.get("first"),
            completionOn: f.get("end"),
            instalmentAmount: f.get("instalment"),
            coveredAmount: f.get("covered"),
            frequency: f.get("frequency"),
            documentId: f.get("documentId") || null,
          })}
        >
          <p>This informal arrangement does not alter the lease, original demand dates or recorded debt. Revisions end the retained arrangement and create a new one.</p>
          {d.account.arrangementOwnerContext&&<label>Legal ownership for this arrangement<select name="ownerInterestId" required defaultValue={d.account.arrangementOwnerContext.owners.find((o:any)=>o.legalName===d.account.legal_owner_name)?.id??''}><option value="">Choose recorded ownership</option>{d.account.arrangementOwnerContext.owners.map((o:any)=><option key={o.id} value={o.id}>{o.legalName??'Legal name missing'} · {o.startDate??'Start unknown'}{o.endDate?` to ${o.endDate}`:''}</option>)}</select></label>}
          <label>
            Start
            <DateOnlyInput name="start" required onChange={arr.setStart} />
          </label>
          <label>
            First payment
            <DateOnlyInput name="first" required value={arr.firstValue} onChange={arr.setFirst} />
          </label>
          <label>
            Expected completion
            <DateOnlyInput name="end" required value={arr.endValue} onChange={arr.setEnd} />
          </label>
          <label>
            Instalment amount
            <input
              name="instalment"
              type="number"
              min=".01"
              step=".01"
              required
              onChange={(e) => arr.setInstalment(e.target.value)}
            />
          </label>
          <label>
            Debt covered
            <input name="covered" type="number" min=".01" step=".01" required onChange={(e) => arr.setCovered(e.target.value)} />
          </label>
          <label>
            Frequency
            <select name="frequency" onChange={(e) => arr.setFrequency(e.target.value)}>
              <option>Weekly</option>
              <option>Fortnightly</option>
              <option>Monthly</option>
              <option>Quarterly</option>
            </select>
          </label>
          <Evidence documents={data.documents ?? []} />
        </ReviewForm>
      )}
      {d.arrangements.map((p: any) => (
        <article key={p.id}>
          {p.ownershipNeedsReview&&p.status==='Active'&&<p role="alert">{p.ownershipReviewReason}</p>}
          {p.recordedOwner&&<p>Arrangement owner: {p.recordedOwner.legalName}. {p.ownershipEvidence}.</p>}
          <p>
            {p.status} arrangement · {money(p.instalment_amount)} {p.frequency}{" "}
            · {p.notes}
          </p>
          {d.arrangement?.id === p.id && (
            <p>
              Expected {money(d.arrangement.expected)}, received{" "}
              {money(d.arrangement.received)}.{" "}
              {d.arrangement.potentiallyMissed
                ? "Possible missed payment — review required."
                : "No shortfall identified."}
            </p>
          )}
          {p.status === "Active" && data.permissions.approve && (
            <ReviewForm
              id={"end-arrangement:" + p.id}
              title="End arrangement"
              path={"/arrangements/" + p.id + "/end"}
              change={change}
              values={(f) => ({
                status: f.get("status"),
                endedOn: f.get("date"),
              })}
            >
              <label>
                Status
                <select name="status">
                  <option>Completed</option>
                  <option>Breached</option>
                  <option>Cancelled</option>
                </select>
              </label>
              <label>
                Effective date
                <DateOnlyInput name="date" defaultValue={today()} required />
              </label>
            </ReviewForm>
          )}
        </article>
      ))}
      {d.account.action_id && (
        <Link to={`/work/actions/${d.account.action_id}`}>
          Open internal arrears review task
        </Link>
      )}
      <details>
        <summary>Allocation and audit history</summary>
        {d.allocations.map((a: any) => (
          <p key={a.id}>
            {formatDateOnly(a.effective_date)} · {money(a.amount)} · {a.method}{" "}
            · {a.reason}
          </p>
        ))}
        {d.events.map((e: any, i: number) => (
          <p key={i}>
            {formatDateTime(e.created_at)} · {e.action} · {e.reason}
          </p>
        ))}
      </details>
    </>
  );
}
function Evidence({ documents }: { documents: any[] }) {
  return (
    <label>
      Supporting document (optional)
      <select name="documentId">
        <option value="">Use recorded provenance</option>
        {documents.map((d) => (
          <option key={d.id} value={d.id}>
            {d.title}
          </option>
        ))}
      </select>
    </label>
  );
}
function ProposalForm({
  building,
  accounts,
  periods,
  documents,
  change,
  root,
  asAt,
}: {
  root: string;
  asAt: string;
  building: string;
  accounts: any[];
  periods: any[];
  documents: any[];
  change: Change;
}) {
  const [accountId, setAccountId] = useState(
      readWorkflowDraft("income-proposal:" + building)?.values.accountId?.[0] ??
        accounts[0]?.id ??
        "",
    ),
    a = accounts.find((x) => x.id === accountId);
  const [effective, setEffective] = useState("");
  const [sources, setSources] = useState<any[]>([]);
  useEffect(() => {
    let active = true;
    setSources([]);
    if (accountId)
      api<any>(
        root +
          "/accounts/" +
          accountId +
          "?building=" +
          building +
          "&asAt=" +
          asAt,
      )
        .then((d) => {
          if (active && d.account.id === accountId && d.building === building)
            setSources(d.entries);
        })
        .catch(() => {});
    return () => {
      active = false;
    };
  }, [accountId, root, asAt]);
  return (
    <ReviewForm
      id={"income-proposal:" + building}
      title="Enter opening balance or adjustment"
      path="/adjustments"
      change={change}
      values={(f) => ({
        accountId: f.get("accountId"),
        periodId: f.get("periodId"),
        kind: f.get("kind"),
        amount: f.get("amount"),
        effectiveDate: f.get("date"),
        dueDate: f.get("due") || null,
        legalOwnerName: f.get("owner"),
        provenance: f.get("provenance"),
        sourceReference: f.get("reference"),
        documentId: f.get("documentId") || null,
        sourcePostingId: f.get("sourcePostingId") || null,
        targetAccountId: f.get("targetAccountId") || null,
        targetPeriodId: f.get("targetPeriodId") || null,
      })}
    >
      <label>
        Account
        <select
          name="accountId"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          required
        >
          {accounts.map((x) => (
            <option value={x.id} key={x.id}>
              {x.premises_name} · {x.schedule_name}
            </option>
          ))}
        </select>
      </label>
      <PeriodSelect
        periods={periods.filter((p) => p.schedule_id === a?.schedule_id)}
        date={effective}
      />
      <label>
        Type
        <select name="kind">
          <option>Opening balance</option>
          <option>Credit</option>
          <option>Adjustment</option>
          <option>Refund record</option>
          <option>Transfer</option>
          <option>Write-off</option>
        </select>
      </label>
      <label>
        Signed amount
        <input type="number" step=".01" name="amount" required />
        <small>Positive is a debit; negative is a credit.</small>
      </label>
      <label>
        Balance / effective date
        <DateOnlyInput name="date" required onChange={setEffective} />
      </label>
      <label>
        Due date
        <DateOnlyInput name="due" />
      </label>
      <label>
        Legal-owner name{a?.legal_owner_name ? " (from the account)" : ""}
        <input
          key={accountId}
          name="owner"
          defaultValue={a?.legal_owner_name ?? ""}
          readOnly={Boolean(a?.legal_owner_name)}
          required
        />
      </label>
      <label>
        Source reference
        <input name="reference" required />
      </label>
      <label>
        Source / provenance
        <input name="provenance" required minLength={3} />
      </label>
      <Evidence documents={documents} />
      <label>
        Source transaction (refund / correction)
        <select name="sourcePostingId">
          <option value="">No source transaction</option>
          {sources
            .filter((p) => p.status === "Posted")
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.posting_type} · {p.external_reference ?? p.narrative} ·{" "}
                {money(
                  p.credit_amount !== "0.00" ? p.credit_amount : p.debit_amount,
                )}
              </option>
            ))}
        </select>
      </label>
      <label>
        Transfer destination
        <select name="targetAccountId">
          <option value="">No transfer</option>
          {accounts
            .filter(
              (x) => x.id !== accountId && x.schedule_id === a?.schedule_id,
            )
            .map((x) => (
              <option key={x.id} value={x.id}>
                {x.premises_name}
              </option>
            ))}
        </select>
      </label>
      <label>
        Transfer destination period
        <select name="targetPeriodId">
          <option value="">No transfer</option>
          {periods
            .filter(
              (p) => p.schedule_id === a?.schedule_id && p.status === "Open",
            )
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
        </select>
      </label>
    </ReviewForm>
  );
}

const pennies = (v: unknown) => {
  const m = String(v ?? "").match(/^(-?)(\d+)(?:\.(\d{0,2}))?$/);
  return m
    ? BigInt(m[2]!) * 100n * (m[1] ? -1n : 1n) +
        BigInt((m[3] ?? "").padEnd(2, "0")) * (m[1] ? -1n : 1n)
    : 0n;
};
const amountText = (n: bigint) =>
  (n < 0n ? "-" : "") +
  (n < 0n ? -n : n) / 100n +
  "." +
  String((n < 0n ? -n : n) % 100n).padStart(2, "0");
type ReceiptPartValue = {
  accountId: string;
  periodId: string;
  amount: string;
  charges: Array<{ postingId: string; amount: string; method: string }>;
};
const blankPart = (): ReceiptPartValue => ({
  accountId: "",
  periodId: "",
  amount: "0.00",
  charges: [],
});
function ReceiptAllocation({
  transaction: t,
  accounts,
  periods,
  root,
  building,
  asAt,
  change,
  canAllocate,
  suggestion,
}: {
  transaction: any;
  accounts: any[];
  periods: any[];
  root: string;
  building: string;
  asAt: string;
  change: Change;
  canAllocate: boolean;
  suggestion?: any;
}) {
  const draftKey = "income-receipt:" + t.id;
  const [parts, setParts] = useState<ReceiptPartValue[]>(() => {
    if (suggestion) {
      const a = accounts.find((a) => a.id === suggestion.accountId);
      return [
        {
          accountId: suggestion.accountId,
          periodId: receiptPeriodForDate(periods, a?.schedule_id, t.transaction_date) ?? "",
          amount: t.available_amount,
          charges: suggestion.charges,
        },
      ];
    }
    try {
      const value = JSON.parse(
        readWorkflowDraft(draftKey)?.values.receiptParts?.[0] ?? "null",
      );
      if (Array.isArray(value) && value.length && value.length <= 100)
        return value;
    } catch {}
    return [blankPart()];
  });
  const remainder =
    pennies(t.available_amount) -
    parts.reduce((n, p) => n + pennies(p.amount), 0n);
  return (
    <article className="income-receipt">
      <h3>Receipt {t.bank_reference ?? t.description}</h3>
      <p>
        {t.account_name} · {formatDateOnly(t.transaction_date)} · remitter{" "}
        {t.counterparty ?? "not recorded"} · available{" "}
        {money(t.available_amount)}
      </p>
      {canAllocate && (
        <>
          <ReviewForm
            id={draftKey}
            title="Record a receipt allocation"
            button="Confirm receipt allocation"
            path="/receipts/allocate"
            change={change}
            values={(f) => ({
              transactionId: t.id,
              effectiveDate: f.get("date"),
              parts,
              multiUnitAuthority: f.get("authority") ?? "",
            })}
          >
            <input
              type="hidden"
              name="receiptParts"
              value={JSON.stringify(parts)}
            />
            <label>
              Effective date
              <DateOnlyInput name="date" defaultValue={t.transaction_date} required />
            </label>
            <div className="full-width">
              {parts.map((part, i) => (
                <ReceiptPart
                  key={i}
                  index={i}
                  value={part}
                  accounts={accounts}
                  periods={periods}
                  root={root}
                  building={building}
                  asAt={asAt}
                  onChange={(value) =>
                    setParts((old) => old.map((p, n) => (n === i ? value : p)))
                  }
                />
              ))}
              <button
                type="button"
                className="secondary"
                disabled={parts.length >= 100}
                onClick={() => setParts((p) => [...p, blankPart()])}
              >
                Add another account
              </button>
              {parts.length > 1 && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setParts((p) => p.slice(0, -1))}
                >
                  Remove last account
                </button>
              )}
              <p role="status">
                Remaining bank receipt: {money(amountText(remainder))}
                {remainder < 0n ? " — reduce the proposed allocation." : ""}
              </p>
            </div>
            <label className="full-width">
              Authority for payments covering several units
              <textarea
                name="authority"
                required={new Set(parts.map((p) => p.accountId)).size > 1}
              />
            </label>
          </ReviewForm>
          <ReviewForm
            id={"income-classify:" + t.id}
            title="Classify non-service-charge or unidentified income"
            path="/receipts/classify"
            change={change}
            values={(f) => ({
              transactionId: t.id,
              amount: f.get("amount"),
              kind: f.get("kind"),
            })}
          >
            <label>
              Classification
              <select name="kind">
                <option>Unidentified</option>
                <option>Insurance proceeds</option>
                <option>Interest</option>
                <option>Reserve transfer</option>
                <option>Non-invoice</option>
                <option>Excluded</option>
              </select>
            </label>
            <label>
              Amount
              <input
                name="amount"
                type="number"
                min=".01"
                step=".01"
                defaultValue={t.available_amount}
                required
              />
            </label>
          </ReviewForm>
          <Link to="/finance/reconciliation">
            Review or reverse non-service-charge bank classifications
          </Link>
        </>
      )}
    </article>
  );
}
function ReceiptPart({
  index,
  value,
  accounts,
  periods,
  root,
  building,
  asAt,
  onChange,
}: {
  index: number;
  value: ReceiptPartValue;
  accounts: any[];
  periods: any[];
  root: string;
  building: string;
  asAt: string;
  onChange: (p: ReceiptPartValue) => void;
}) {
  const [detail, setDetail] = useState<any>(),
    [error, setError] = useState<unknown>(),
    a = accounts.find((x) => x.id === value.accountId);
  useEffect(() => {
    let active = true;
    setDetail(undefined);
    setError(undefined);
    if (value.accountId)
      api<any>(
        root +
          "/accounts/" +
          value.accountId +
          "?building=" +
          building +
          "&asAt=" +
          asAt,
      )
        .then((d) => {
          if (active) {
            if (
              d.building !== building ||
              d.account.id !== value.accountId ||
              d.entries.some((p: any) => p.account_id !== value.accountId)
            )
              throw Error("Account context changed.");
            setDetail(d);
          }
        })
        .catch((e) => {
          if (active) setError(e);
        });
    return () => {
      active = false;
    };
  }, [value.accountId, root, building, asAt]);
  const unapplied =
    pennies(value.amount) -
    value.charges.reduce((n, c) => n + pennies(c.amount), 0n);
  return (
    <fieldset>
      <legend>Account allocation {index + 1}</legend>
      <label>
        Account
        <select
          name={"receiptAccount:" + index}
          value={value.accountId}
          required
          onChange={(e) =>
            onChange({ ...blankPart(), accountId: e.target.value })
          }
        >
          <option value="">Choose verified account</option>
          {accounts.map((x) => (
            <option key={x.id} value={x.id}>
              {x.premises_name} · {x.legal_owner_name} · {x.schedule_name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Period
        <select
          name={"receiptPeriod:" + index}
          value={value.periodId}
          required
          onChange={(e) => onChange({ ...value, periodId: e.target.value })}
        >
          <option value="">Choose period</option>
          {receiptPeriods(periods, a?.schedule_id)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
        </select>
      </label>
      <label>
        Amount credited to this account
        <input
          name={"receiptAmount:" + index}
          type="number"
          min=".01"
          step=".01"
          value={value.amount}
          required
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
        />
      </label>
      {Boolean(error) && <ErrorState error={error} />}
      <p>
        Proposed on-account credit {money(amountText(unapplied))}.{" "}
        {detail && (
          <>
            Resulting account balance{" "}
            {money(amountText(pennies(detail.balance) - pennies(value.amount)))}
            .
          </>
        )}
      </p>
      {detail?.charges
        .filter((c: any) => pennies(c.remaining_amount) > 0n)
        .map((c: any) => (
          <label key={c.id}>
            {c.demand_reference ?? c.posting_type} · due{" "}
            {formatDateOnly(c.due_date)} · paid {money(c.paid_amount)} ·
            remaining {money(c.remaining_amount)}
            {pennies(c.disputed_amount) > 0n ? " · Disputed" : ""}
            <input
              name={"charge:" + index + ":" + c.id}
              type="number"
              min="0"
              step=".01"
              disabled={pennies(c.disputed_amount) > 0n}
              value={
                value.charges.find((p) => p.postingId === c.id)?.amount ??
                "0.00"
              }
              onChange={(e) =>
                onChange({
                  ...value,
                  charges: [
                    ...value.charges.filter((p) => p.postingId !== c.id),
                    ...(pennies(e.target.value) > 0n
                      ? [
                          {
                            postingId: c.id,
                            amount: e.target.value,
                            method: "Manual",
                          },
                        ]
                      : []),
                  ],
                })
              }
            />
          </label>
        ))}
    </fieldset>
  );
}
